import { useEffect, useMemo } from 'react';

import { useFlowState } from '@betterment/js-runtime';
import { Banner, Box, Button, H1, Separator, Text, ToggleTip } from '@betterment/samba-react';
import { Form, useFormikContext } from 'formik';

import { DeferralRow } from './DeferralRow';

import { DeferralSettings, DeferralSettingsFlowState, FourOhOneKAccounts, PayType } from '../../types';

const SectionTitle = ({ children }: { children: React.ReactNode; addon?: React.ReactNode }) => (
  <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.100', pb: 'space.300' }}>{children}</Box>
);

const DeferralFields = ({
  payType,
  values,
  loading,
}: {
  payType: PayType;
  values: FourOhOneKAccounts;
  loading: boolean;
}) => {
  return (
    <>
      <DeferralRow
        payType={payType}
        accountType="traditional"
        label="Traditional"
        caption="Pre-tax"
        values={values}
        loading={loading}
      />
      {values.roth && (
        <DeferralRow
          payType={payType}
          accountType="roth"
          label="Roth"
          caption="Post-tax"
          values={values}
          loading={loading}
        />
      )}
    </>
  );
};

export const DeferralForm = ({ loading, priorRate }: { loading: boolean; priorRate?: DeferralSettings }) => {
  const { values, setValues } = useFormikContext<DeferralSettings>();
  const {
    flowState: { values: flowValues },
  } = useFlowState<DeferralSettingsFlowState>();

  useEffect(() => {
    if (flowValues.newRate) {
      setValues(flowValues.newRate);
    }
  }, [flowValues, setValues]);

  const hasStoppedContributing = useMemo(() => {
    if (!priorRate) return false;

    const hasPriorContributionPositiveRates =
      priorRate.base.traditional.value > 0 ||
      (priorRate.base.roth?.value ?? 0) > 0 ||
      (priorRate.bonus?.traditional.value ?? 0) > 0 ||
      (priorRate.bonus?.roth?.value ?? 0) > 0;

    const isNotContributing =
      values.base.traditional.value === 0 &&
      (values.base.roth?.value ?? 0) === 0 &&
      (values.bonus?.traditional.value ?? 0) === 0 &&
      (values.bonus?.roth?.value ?? 0) === 0;

    return hasPriorContributionPositiveRates && isNotContributing;
  }, [priorRate, values]);

  return (
    <Form>
      <Box>
        <SectionTitle>
          <H1 variant="title200">Base pay (per pay period)</H1>
        </SectionTitle>
        <DeferralFields payType="base" values={values.base} loading={loading} />
        {hasStoppedContributing && (
          <Banner variant="warning" css={{ mt: 'space.200' }}>
            <Banner.Content>
              <Text>Are you sure you want to stop contributing to your 401(k)?</Text>
            </Banner.Content>
          </Banner>
        )}
      </Box>
      {values.bonus && (
        <>
          <Separator css={{ my: 'space.300' }} />

          <Box>
            <SectionTitle>
              <H1 variant="title200">Bonus</H1>
              <ToggleTip>
                <ToggleTip.Trigger iconName="InfoCircle" label="Toggle Tip" />
                <ToggleTip.Content side="right" css={{ maxWidth: '300px' }}>
                  Bonus pay and timing are set by your employer. Reach out to them if you have questions.
                </ToggleTip.Content>
              </ToggleTip>
            </SectionTitle>
            <DeferralFields payType="bonus" values={values.bonus} loading={loading} />
          </Box>
        </>
      )}
      <Button type="submit" value="Continue" css={{ mt: 'space.300' }} disabled={loading}>
        Continue
      </Button>
    </Form>
  );
};
